import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { verifyDocumentExam } from "@/services/n8n/verifyDocumentExam";
import { examAIExtraction } from "@/services/n8n/examAIExtraction";
import { ExtractedAnalyte, ExtractedExam } from "../../../hooks/types";
import { useExamSave } from "../../../hooks/useExamSave";

interface UseExamExtractionProps {
  handleExamExtraction: (selectedAnalytes: ExtractedAnalyte[]) => Promise<any>;
  handleAddExtractedExam: (exams: ExtractedExam[]) => void;
  selectedRequestId: string | null;
  noLinking: boolean;
  onExamSaved?: () => void;
}

export const useExamExtraction = ({
  handleExamExtraction,
  handleAddExtractedExam,
  selectedRequestId,
  noLinking,
  onExamSaved,
}: UseExamExtractionProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [extractedAnalytes, setExtractedAnalytes] = useState<
    ExtractedAnalyte[]
  >([]);
  const [isInvalidDocument, setIsInvalidDocument] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { handleSaveExam } = useExamSave({
    onSuccess: onExamSaved,
  });

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setShowDialog(false);
    setFileBase64(null);
    setIsInvalidDocument(false);
    setExtractionError(null);
  };

  const handleExtractionProcess = async (base64: string) => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setIsExtracting(true);
      setExtractionError(null);
      toast.info("Processando com IA...");

      const aiResult = await examAIExtraction(base64);

      if (signal.aborted) return;

      if (!aiResult.success) {
        console.error("Erro na extração com IA:", aiResult.error);
        setExtractionError(
          "Ocorreu um erro durante a extração dos analitos. Por favor, tente novamente."
        );
        return;
      }

      const processedData = await handleExamExtraction(aiResult.data);

      if (!processedData.results || processedData.results.length === 0) {
        setExtractionError(
          "Não foi possível identificar analitos neste documento."
        );
        return;
      }

      const extractedData = processedData?.results.map(
        (exam: ExtractedExam) => ({
          ...exam,
          selected: false,
        })
      );

      if (signal.aborted) return;
      setExtractedAnalytes(extractedData);
      setShowDialog(true);
    } catch (error: any) {
      if (error.name === "AbortError" || signal.aborted) {
        return;
      }
      console.error("Erro na extração:", error);
      setExtractionError(
        "Ocorreu um erro inesperado durante a extração. Por favor, tente novamente."
      );
      toast.error("Erro ao extrair os dados do exame");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setIsVerifying(true);
      setIsInvalidDocument(false);
      toast.info("Verificando documento...");

      const result = await verifyDocumentExam(file);
      const base64 = await convertToBase64(file);

      if (signal.aborted) return;

      if (result?.status === "success") {
        setFileBase64(base64);
        setShowDialog(true);
      } else {
        setIsInvalidDocument(true);
        setFileBase64(base64);
        toast.warning("O arquivo pode não ser um resultado de exame válido");
      }
    } catch (error: any) {
      if (error.name === "AbortError" || signal.aborted) {
        return;
      }
      console.error("Erro ao processar documento:", error);
      toast.error("Ocorreu um erro ao verificar o documento");
      handleClose();
    } finally {
      setIsVerifying(false);
    }
  };

  // Nova função específica para iniciar a extração manualmente
  const handleStartExtraction = () => {
    if (fileBase64) {
      handleExtractionProcess(fileBase64);
    }
  };

  // Função específica para exames existentes
  const handleExistingExam = async (base64: string) => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setShowDialog(true);
      setFileBase64(base64);
    } catch (error: any) {
      if (error.name === "AbortError" || signal.aborted) {
        return;
      }
      console.error("Erro ao processar exame existente:", error);
      toast.error("Erro ao processar o exame. Tente novamente.");
      handleClose();
    }
  };

  const handleRetryExtraction = () => {
    if (fileBase64) {
      handleExtractionProcess(fileBase64);
    }
  };

  const handleProceedAnyway = () => {
    if (fileBase64) {
      setIsInvalidDocument(false);
      setShowDialog(true);
      handleExtractionProcess(fileBase64);
    }
  };

  const handleAnalyteSelection = (index: number) => {
    setExtractedAnalytes((prev) =>
      prev.map((analyte, i) =>
        i === index ? { ...analyte, selected: !analyte.selected } : analyte
      )
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setExtractedAnalytes((prev) =>
      prev.map((analyte) => ({ ...analyte, selected: checked }))
    );
  };

  const handleAddSelectedAnalytes = async () => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const filteredAnalytes = extractedAnalytes.filter(
        (analyte) => analyte.selected
      );

      if (filteredAnalytes.length > 0) {
        handleAddExtractedExam(filteredAnalytes);
      }

      // Salvar o exame apenas se estiver vinculado a uma solicitação
      if (selectedRequestId && !noLinking && fileBase64) {
        const saveResult = await handleSaveExam(selectedRequestId, fileBase64);
        if (!saveResult) {
          toast.error("Erro ao salvar o resultado do exame");
          return;
        }
      }

      toast.success("Resultado do exame salvo com sucesso!");
      handleClose();
    } catch (error: any) {
      if (error.name === "AbortError" || signal.aborted) {
        return;
      }
      console.error("Erro ao salvar resultado:", error);
      toast.error("Ocorreu um erro ao salvar o resultado");
    } finally {
      setIsExtracting(false);
    }
  };

  return {
    isVerifying,
    isExtracting,
    fileBase64,
    showDialog,
    extractedAnalytes,
    isInvalidDocument,
    extractionError,
    handleClose,
    handleFileUpload,
    handleRetryExtraction,
    handleProceedAnyway,
    handleAnalyteSelection,
    handleSelectAll,
    handleAddSelectedAnalytes,
    handleExistingExam,
    handleExtractionProcess,
    handleStartExtraction,
    setFileBase64,
    setShowDialog,
  };
};
